import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Building2 } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await authAPI.login({ email, password });
      localStorage.setItem('ccip_token', response.data.access_token);
      localStorage.setItem('ccip_user', JSON.stringify(response.data.user));
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#09409C] via-[#146CBB] to-[#229644] p-4">
      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-[#09409C] to-[#146CBB] flex items-center justify-center">
            <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <CardTitle className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <span className="text-gradient-primary">PolarisIQ</span>
          </CardTitle>
          <CardDescription className="text-base">Navigate Every Renewal</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-[#09409C] to-[#146CBB] hover:from-[#072e70] hover:to-[#105a99] transition-all"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
{/* Register link hidden for now
          <p className="text-center text-sm text-muted-foreground mt-6">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#09409C] hover:text-[#146BBB] font-medium">
              Register
            </Link>
          </p>
*/}
        </CardContent>
      </Card>

      {/* Powered by Resolvetech Badge */}
      <a 
        href="https://www.resolvetech.com" 
        target="_blank" 
        rel="noopener noreferrer"
        className="fixed bottom-3 right-3 z-[10000] bg-white rounded-full shadow-lg px-3 py-1.5 flex items-center gap-2 border border-gray-200 hover:shadow-xl transition-shadow"
        data-testid="resolvetech-badge"
      >
        <span className="text-xs text-gray-500 font-medium">Powered by</span>
        <img 
          src="https://customer-assets.emergentagent.com/job_energy-forecast-10/artifacts/9xwekntx_rtslogo-with-name-transparent-300-ppi-RGB.png" 
          alt="Resolvetech" 
          className="h-5 w-auto"
        />
      </a>
    </div>
  );
}
